/**
 * Todo / Task Service — Firestore CRUD for the `todos` collection.
 *
 * Powers the "Plate" task system: the dashboard inbox widget, the legacy
 * dashboard todo panel, and the dedicated Plate page. Supports @mention
 * assignment, priorities, a To Do / In Progress / Blocked / Done workflow,
 * due dates + reminders, and an activity/comment subcollection.
 *
 * Back-compat: the collection is unchanged (`todos`). All new fields are
 * optional and `normalizeTask()` fills sensible defaults so pre-existing docs
 * (no `status` / `priority` / `assigneeIds`) render correctly with zero
 * migration. Writes lazily backfill the denormalised fields; the one-shot
 * `src/scripts/backfill-tasks.ts` stamps the historical tail.
 */

import { db } from '@/firebase';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  deleteField,
  doc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  writeBatch,
  increment,
  arrayUnion,
  arrayRemove,
  runTransaction,
} from 'firebase/firestore';
import type {
  TodoItem,
  TodoAssignee,
  TaskStatus,
  TaskPriority,
  TaskActivity,
} from '@/types/schema';
import { tenantIdReadUnion, getTenantIdSync } from '@/lib/tenant';
import { sourceTypeToEntityKind } from '@/config/entityThemes';

const COLLECTION = 'todos';

// ============================================================================
// NORMALISATION (back-compat reader)
// ============================================================================

const STATUS_VALUES: readonly TaskStatus[] = ['todo', 'in_progress', 'blocked', 'done'];
const PRIORITY_VALUES: readonly TaskPriority[] = ['urgent', 'high', 'normal', 'low'];

/**
 * Fill defaults on a raw todo doc so legacy docs (no status / priority /
 * assigneeIds / entityKind) behave like fully-migrated ones. Pure — no writes.
 * EVERY read path should run docs through this.
 *
 * Non-canonical status/priority values (hand-edited docs, future enum drift)
 * are coerced back to canonical so fixed-key grouping (byStatus / byPriority)
 * can never silently drop a task.
 */
export function normalizeTask(raw: TodoItem): TodoItem {
  const validStatus =
    raw.status && STATUS_VALUES.includes(raw.status) ? raw.status : undefined;
  const status: TaskStatus = validStatus ?? (raw.completed ? 'done' : 'todo');
  const priority: TaskPriority =
    raw.priority && PRIORITY_VALUES.includes(raw.priority) ? raw.priority : 'normal';
  return {
    ...raw,
    status,
    completed: raw.completed ?? status === 'done',
    priority,
    entityKind: raw.entityKind ?? sourceTypeToEntityKind(raw.sourceType),
    assigneeIds: raw.assigneeIds ?? raw.assignees?.map((a) => a.userId) ?? [],
    statusOrder: raw.statusOrder ?? raw.order ?? 0,
    lastActivityAt: raw.lastActivityAt ?? raw.updatedAt ?? raw.createdAt,
  };
}

// ============================================================================
// CREATE
// ============================================================================

export async function createTodo(
  todo: Omit<TodoItem, 'id' | 'createdAt' | 'updatedAt'>
): Promise<string> {
  const now = new Date().toISOString();
  const status: TaskStatus = todo.status ?? (todo.completed ? 'done' : 'todo');
  const assigneeIds = todo.assigneeIds ?? todo.assignees?.map((a) => a.userId) ?? [];
  const entityKind = todo.entityKind ?? sourceTypeToEntityKind(todo.sourceType);

  const payload = {
    ...todo,
    status,
    completed: status === 'done',
    assigneeIds,
    entityKind,
    statusOrder: todo.statusOrder ?? todo.order ?? 0,
    lastActivityAt: now,
    createdAt: now,
    updatedAt: now,
  };

  // Strip undefined values — Firestore rejects them
  const sanitized = Object.fromEntries(
    Object.entries(payload).filter(([, v]) => v !== undefined)
  );
  const docRef = await addDoc(collection(db, COLLECTION), sanitized);
  return docRef.id;
}

// ============================================================================
// UPDATE
// ============================================================================

export async function updateTodo(
  todoId: string,
  updates: Partial<TodoItem>
): Promise<void> {
  const now = new Date().toISOString();
  const patch: Record<string, unknown> = {
    ...updates,
    updatedAt: now,
    lastActivityAt: now,
  };
  // Keep the denormalised assignee mirror in lock-step.
  if (updates.assignees) {
    patch.assigneeIds = updates.assignees.map((a) => a.userId);
  }
  // Keep the status <-> completed mirror intact on partial patches.
  if (updates.status !== undefined && updates.completed === undefined) {
    patch.completed = updates.status === 'done';
  }
  if (updates.completed !== undefined && updates.status === undefined) {
    patch.status = updates.completed ? 'done' : 'todo';
  }
  // Strip undefined values — Firestore rejects them.
  const sanitized = Object.fromEntries(
    Object.entries(patch).filter(([, v]) => v !== undefined)
  );
  await updateDoc(doc(db, COLLECTION, todoId), sanitized);
}

/**
 * Complete / re-open a task. Keeps the `status` mirror in sync
 * (completed ⇒ 'done'; re-open ⇒ 'todo').
 */
export async function toggleTodo(
  todoId: string,
  completed: boolean,
  userId: string
): Promise<void> {
  const now = new Date().toISOString();
  if (completed) {
    await updateDoc(doc(db, COLLECTION, todoId), {
      completed: true,
      status: 'done' as TaskStatus,
      completedAt: now,
      completedBy: userId,
      lastActivityAt: now,
      updatedAt: now,
    });
  } else {
    await updateDoc(doc(db, COLLECTION, todoId), {
      completed: false,
      status: 'todo' as TaskStatus,
      completedAt: deleteField(),
      completedBy: deleteField(),
      lastActivityAt: now,
      updatedAt: now,
    });
  }
}

/**
 * Move a task to an explicit workflow status (board drag / status picker).
 * Keeps the `completed` mirror + completion audit in sync and logs the change.
 */
export async function updateTaskStatus(
  todoId: string,
  status: TaskStatus,
  actor: { id: string; name: string },
  fromStatus?: TaskStatus
): Promise<void> {
  const now = new Date().toISOString();
  const isDone = status === 'done';
  await updateDoc(doc(db, COLLECTION, todoId), {
    status,
    completed: isDone,
    ...(isDone
      ? { completedAt: now, completedBy: actor.id }
      : { completedAt: deleteField(), completedBy: deleteField() }),
    lastActivityAt: now,
    updatedAt: now,
  });
  await appendActivity(todoId, {
    type: 'status_change',
    authorId: actor.id,
    authorName: actor.name,
    meta: { to: status, ...(fromStatus ? { from: fromStatus } : {}) },
  });
}

export async function updateTaskPriority(
  todoId: string,
  priority: TaskPriority,
  actor: { id: string; name: string }
): Promise<void> {
  const now = new Date().toISOString();
  await updateDoc(doc(db, COLLECTION, todoId), {
    priority,
    lastActivityAt: now,
    updatedAt: now,
  });
  await appendActivity(todoId, {
    type: 'priority_change',
    authorId: actor.id,
    authorName: actor.name,
    meta: { to: priority },
  });
}

export async function setTaskDueDate(
  todoId: string,
  dueDate: string | null,
  actor: { id: string; name: string }
): Promise<void> {
  const now = new Date().toISOString();
  await updateDoc(doc(db, COLLECTION, todoId), {
    dueDate: dueDate ?? deleteField(),
    lastActivityAt: now,
    updatedAt: now,
  });
  await appendActivity(todoId, {
    type: 'due_change',
    authorId: actor.id,
    authorName: actor.name,
    meta: { to: dueDate ?? '(cleared)' },
  });
}

/** Set / clear a reminder time. Clears the sent-guard so it fires again. */
export async function setTaskReminder(
  todoId: string,
  reminderAt: string | null
): Promise<void> {
  await updateDoc(doc(db, COLLECTION, todoId), {
    reminderAt: reminderAt ?? deleteField(),
    reminderSent: false,
    updatedAt: new Date().toISOString(),
  });
}

export async function addWatcher(todoId: string, userId: string): Promise<void> {
  await updateDoc(doc(db, COLLECTION, todoId), {
    watcherIds: arrayUnion(userId),
    updatedAt: new Date().toISOString(),
  });
}

export async function removeWatcher(todoId: string, userId: string): Promise<void> {
  await updateDoc(doc(db, COLLECTION, todoId), {
    watcherIds: arrayRemove(userId),
    updatedAt: new Date().toISOString(),
  });
}

/**
 * Add ONE assignee concurrent-safely via a TRANSACTION.
 *
 * Why not arrayUnion: (a) on a legacy doc that doesn't carry `assigneeIds`
 * yet, arrayUnion creates the field with ONLY the new element — instantly
 * hiding the task from every existing assignee's My Plate; (b) the assignee
 * object embeds a volatile `addedAt`, so two concurrent adds of the SAME
 * person produce two distinct objects (arrayUnion can't dedup them) while
 * the id mirror dedups — a permanent mirror desync. The transaction reads
 * the live doc, dedups by userId, and recomputes BOTH mirrors from the full
 * array, so concurrent edits serialize and legacy docs heal on first touch.
 *
 * @returns true if the assignee was actually added (false = already assigned)
 */
export async function addTaskAssignee(
  todoId: string,
  assignee: TodoAssignee,
  actor?: { id: string; name: string }
): Promise<boolean> {
  const now = new Date().toISOString();
  const ref = doc(db, COLLECTION, todoId);
  let added = false;

  await runTransaction(db, async (txn) => {
    added = false; // reset per attempt — retries must not leak state
    const snap = await txn.get(ref);
    if (!snap.exists()) return;
    const data = snap.data() as TodoItem;
    const current = data.assignees ?? [];
    if (current.some((a) => a.userId === assignee.userId)) return; // idempotent
    const next = [...current, assignee];
    txn.update(ref, {
      assignees: next,
      assigneeIds: next.map((a) => a.userId),
      lastActivityAt: now,
      updatedAt: now,
    });
    added = true;
  });

  if (added && actor) {
    await appendActivity(todoId, {
      type: 'assignee_change',
      authorId: actor.id,
      authorName: actor.name,
      meta: { to: `+ ${assignee.userName}` },
    });
  }
  return added;
}

/**
 * Remove ONE assignee (by userId) concurrent-safely via a TRANSACTION.
 * Filters EVERY entry with that userId and recomputes both mirrors — so it
 * also self-heals any duplicate/ghost entries and legacy docs missing the
 * `assigneeIds` field.
 *
 * @returns true if anything changed
 */
export async function removeTaskAssignee(
  todoId: string,
  userId: string,
  userName: string,
  actor?: { id: string; name: string }
): Promise<boolean> {
  const now = new Date().toISOString();
  const ref = doc(db, COLLECTION, todoId);
  let removed = false;

  await runTransaction(db, async (txn) => {
    removed = false; // reset per attempt
    const snap = await txn.get(ref);
    if (!snap.exists()) return;
    const data = snap.data() as TodoItem;
    const current = data.assignees ?? [];
    const next = current.filter((a) => a.userId !== userId);
    const ids = next.map((a) => a.userId);
    const idsChanged =
      JSON.stringify(ids) !== JSON.stringify(data.assigneeIds ?? []);
    if (next.length === current.length && !idsChanged) return; // nothing to do
    txn.update(ref, {
      assignees: next,
      assigneeIds: ids,
      lastActivityAt: now,
      updatedAt: now,
    });
    removed = next.length !== current.length;
  });

  if (removed && actor) {
    await appendActivity(todoId, {
      type: 'assignee_change',
      authorId: actor.id,
      authorName: actor.name,
      meta: { to: `− ${userName}` },
    });
  }
  return removed;
}

/**
 * Replace the assignee list. Recomputes the `assigneeIds` mirror and logs it.
 */
export async function updateTodoAssignees(
  todoId: string,
  assignees: TodoAssignee[],
  actor?: { id: string; name: string }
): Promise<void> {
  const now = new Date().toISOString();
  await updateDoc(doc(db, COLLECTION, todoId), {
    assignees,
    assigneeIds: assignees.map((a) => a.userId),
    lastActivityAt: now,
    updatedAt: now,
  });
  if (actor) {
    await appendActivity(todoId, {
      type: 'assignee_change',
      authorId: actor.id,
      authorName: actor.name,
      meta: { to: assignees.map((a) => a.userName).join(', ') || '(none)' },
    });
  }
}

// ============================================================================
// DELETE
// ============================================================================

export async function deleteTodo(todoId: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTION, todoId));
}

// ============================================================================
// REORDER
// ============================================================================

/** Legacy manual sort (kept for back-compat — writes `order`). */
export async function reorderTodos(
  orderedIds: { id: string; order: number }[]
): Promise<void> {
  const batch = writeBatch(db);
  const now = new Date().toISOString();
  orderedIds.forEach(({ id, order }) => {
    batch.update(doc(db, COLLECTION, id), { order, updatedAt: now });
  });
  await batch.commit();
}

/** Persist the vertical order of cards within a board column (`statusOrder`). */
export async function reorderTasksInColumn(
  orderedIds: { id: string; statusOrder: number }[]
): Promise<void> {
  const batch = writeBatch(db);
  const now = new Date().toISOString();
  orderedIds.forEach(({ id, statusOrder }) => {
    batch.update(doc(db, COLLECTION, id), { statusOrder, updatedAt: now });
  });
  await batch.commit();
}

// ============================================================================
// ACTIVITY / COMMENTS (subcollection: todos/{id}/activity)
// ============================================================================

/** Write one activity/timeline entry for a task. */
export async function appendActivity(
  todoId: string,
  activity: Omit<TaskActivity, 'id' | 'createdAt' | 'tenantId'> & { tenantId?: string }
): Promise<string> {
  const now = new Date().toISOString();
  const tenantId = activity.tenantId ?? getTenantIdSync();
  const payload = Object.fromEntries(
    Object.entries({ ...activity, tenantId, createdAt: now }).filter(
      ([, v]) => v !== undefined
    )
  );
  const ref = await addDoc(collection(db, COLLECTION, todoId, 'activity'), payload);
  return ref.id;
}

/** Add a comment (bumps the denormalised commentCount + lastActivityAt). */
export async function addTaskComment(
  todoId: string,
  comment: { authorId: string; authorName: string; body: string; tenantId?: string }
): Promise<string> {
  const now = new Date().toISOString();
  const id = await appendActivity(todoId, {
    type: 'comment',
    authorId: comment.authorId,
    authorName: comment.authorName,
    body: comment.body,
    tenantId: comment.tenantId,
  });
  await updateDoc(doc(db, COLLECTION, todoId), {
    commentCount: increment(1),
    lastActivityAt: now,
    updatedAt: now,
  });
  return id;
}

export function subscribeActivity(
  todoId: string,
  callback: (entries: TaskActivity[]) => void
): () => void {
  let unsubscribeFallback: (() => void) | null = null;

  const emit = (docs: import('firebase/firestore').QueryDocumentSnapshot[]) =>
    callback(
      docs
        .map((d) => ({ id: d.id, ...d.data() } as TaskActivity))
        .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
    );

  const q = query(
    collection(db, COLLECTION, todoId, 'activity'),
    orderBy('createdAt', 'asc')
  );
  const unsubscribePrimary = onSnapshot(
    q,
    (snap) => emit(snap.docs),
    () => {
      // Missing index / permission — fall back to unordered + client sort.
      // Capture the fallback unsubscribe so the returned cleanup tears BOTH
      // listeners down (mirrors subscribeTodos — an uncaptured fallback leaks).
      const fb = query(collection(db, COLLECTION, todoId, 'activity'));
      unsubscribeFallback = onSnapshot(fb, (snap) => emit(snap.docs));
    }
  );

  return () => {
    unsubscribePrimary();
    unsubscribeFallback?.();
  };
}

// ============================================================================
// SUBSCRIBE — dashboard "My Plate" (assignee-filtered)
// ============================================================================

export function subscribeTodos(
  userId: string,
  tenantId: string,
  callback: (todos: TodoItem[]) => void
): () => void {
  let unsubscribeFallback: (() => void) | null = null;

  // Reads tenant todos and filters by assignee CLIENT-side. `todos` has no
  // per-assignee server index for legacy docs (which pre-date `assigneeIds`),
  // so a broad tenant read + client filter is the migration-safe path until
  // `backfill-tasks.ts` has stamped every doc. Once the whole collection
  // carries `assigneeIds`, this can flip to
  // `where('assigneeIds', 'array-contains', userId)` (indexes already declared
  // in firestore.indexes.json).
  const q = query(
    collection(db, COLLECTION),
    where('tenantId', 'in', tenantIdReadUnion(tenantId)),
    orderBy('createdAt', 'desc'),
    limit(300)
  );

  const filterForUser = (
    docs: import('firebase/firestore').QueryDocumentSnapshot[]
  ) =>
    docs
      .map((d) => normalizeTask({ id: d.id, ...d.data() } as TodoItem))
      .filter((t) => t.assigneeIds?.includes(userId));

  const unsubscribePrimary = onSnapshot(
    q,
    (snapshot) => {
      callback(filterForUser(snapshot.docs));
    },
    (err) => {
      console.error('Error subscribing to todos:', err);
      const fallbackQ = query(
        collection(db, COLLECTION),
        where('tenantId', 'in', tenantIdReadUnion(tenantId)),
        limit(300)
      );
      unsubscribeFallback = onSnapshot(fallbackQ, (snapshot) => {
        const todos = filterForUser(snapshot.docs).sort((a, b) =>
          b.createdAt.localeCompare(a.createdAt)
        );
        callback(todos);
      });
    }
  );

  return () => {
    unsubscribePrimary();
    unsubscribeFallback?.();
  };
}

// ============================================================================
// SUBSCRIBE — full Plate page (all tenant tasks, grouped/filtered in the hook)
// ============================================================================

/**
 * Subscribe to ALL tasks for the tenant (bounded to the newest 500 by
 * createdAt — the (tenantId, createdAt DESC) composite index already exists).
 * Without the orderBy, Firestore would return an ARBITRARY 500 docs in
 * document-id order once the collection grows past the cap, silently dropping
 * recent tasks. The Plate page groups / filters client-side; display sort is
 * by lastActivityAt.
 */
export function subscribeTasksForScope(
  tenantId: string,
  callback: (tasks: TodoItem[]) => void
): () => void {
  let unsubscribeFallback: (() => void) | null = null;

  const emit = (docs: import('firebase/firestore').QueryDocumentSnapshot[]) => {
    const tasks = docs
      .map((d) => normalizeTask({ id: d.id, ...d.data() } as TodoItem))
      .sort((a, b) =>
        (b.lastActivityAt || b.createdAt).localeCompare(a.lastActivityAt || a.createdAt)
      );
    callback(tasks);
  };

  const q = query(
    collection(db, COLLECTION),
    where('tenantId', 'in', tenantIdReadUnion(tenantId)),
    orderBy('createdAt', 'desc'),
    limit(500)
  );
  const unsubscribePrimary = onSnapshot(
    q,
    (snap) => emit(snap.docs),
    (err) => {
      console.error('Error subscribing to tasks (scope):', err);
      // Missing index — fall back to unordered (arbitrary cap, but functional).
      const fb = query(
        collection(db, COLLECTION),
        where('tenantId', 'in', tenantIdReadUnion(tenantId)),
        limit(500)
      );
      unsubscribeFallback = onSnapshot(fb, (snap) => emit(snap.docs));
    }
  );

  return () => {
    unsubscribePrimary();
    unsubscribeFallback?.();
  };
}
